import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@pypestream/design-system';
import { FC, Fragment, memo } from 'react';
import './permissions-table.scss';
import { TableRowContent } from './table-row-content';
import {
  HandleAddProject,
  HandleAddTeam,
  HandleAllOrgsCollapse,
  HandleOrgCollapse,
  HandleRemoveProjectWithRoles,
  HandleRemoveTeam,
  HandleToggleFavoriteOrg,
  HandleUpdateManagerRole,
  HandleUpdateProjectProductRole,
  ShortOrgType,
} from './permissions-table.types';

export type PermissionsTableProps = {
  orgs: ShortOrgType[];
  canManageAccess: boolean;
  isAddingTeamToOrg?: boolean;
  isSavingDetails: boolean;
  isPypestreamDataTeamEligible: boolean;
  favoriteOrgIds: string[];
  canEditFavorites: boolean;
  handleAllOrgsCollapse: HandleAllOrgsCollapse;
  handleOrgCollapse: HandleOrgCollapse;
  handleUpdateManagerRole: HandleUpdateManagerRole;
  handleRemoveTeam?: HandleRemoveTeam;
  handleAddTeam?: HandleAddTeam;
  handleUpdateProjectProductRole: HandleUpdateProjectProductRole;
  handleAddProject: HandleAddProject;
  handleRemoveProjectWithRoles: HandleRemoveProjectWithRoles;
  handleToggleFavoriteOrg: HandleToggleFavoriteOrg;
};

export const PermissionsTable: FC<PermissionsTableProps> = ({
  orgs,
  canManageAccess,
  isAddingTeamToOrg,
  isSavingDetails,
  isPypestreamDataTeamEligible,
  favoriteOrgIds,
  canEditFavorites,
  handleAllOrgsCollapse,
  handleOrgCollapse,
  handleUpdateManagerRole,
  handleRemoveTeam,
  handleAddTeam,
  handleUpdateProjectProductRole,
  handleAddProject,
  handleRemoveProjectWithRoles,
  handleToggleFavoriteOrg,
}) => {
  return (
    <Table size="large" bordered className="c-permissions-table">
      <TableHead>
        <TableRow className="background-gray">
          <TableCell
            variant="th"
            i18nKey="manager/common:permissionAndAccessTable.orgTitle"
          >
            <div className="table-expand-collapse-button">
              {orgs.some((org) => !org.isCollapsed) ? (
                <IconButton
                  name="chevrons-down-up"
                  size="xsmall"
                  onClick={() => {
                    handleAllOrgsCollapse({ collapse: true });
                  }}
                />
              ) : (
                <IconButton
                  name="chevrons-up-down"
                  size="xsmall"
                  onClick={() => {
                    handleAllOrgsCollapse({ collapse: false });
                  }}
                />
              )}
              <strong>Org</strong>
            </div>
          </TableCell>
          <TableCell
            variant="th"
            i18nKey="manager/common:permissionAndAccessTable.projectTitle"
          >
            <strong>Project</strong>
          </TableCell>
          <TableCell
            variant="th"
            i18nKey="manager/common:permissionAndAccessTable.toolTitle"
          >
            {orgs[0]?.isCollapsed ? 'Tool' : <strong>Tool</strong>}
          </TableCell>
          <TableCell
            variant="th"
            i18nKey="manager/common:permissionAndAccessTable.roleTitle"
          >
            {orgs[0]?.isCollapsed ? 'Role' : <strong>Role</strong>}
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {orgs.map((org: ShortOrgType) => (
          <Fragment key={org.id}>
            <TableRowContent
              key={org.id}
              org={org}
              isAddingTeamToOrg={!!isAddingTeamToOrg}
              canManageAccess={org.canManageAccess ?? canManageAccess}
              isSavingDetails={isSavingDetails}
              isPypestreamDataTeamEligible={isPypestreamDataTeamEligible}
              favoriteOrgIds={favoriteOrgIds}
              canEditFavorites={canEditFavorites}
              handleOrgCollapse={handleOrgCollapse}
              handleUpdateManagerRole={handleUpdateManagerRole}
              handleRemoveTeam={handleRemoveTeam}
              handleAddTeam={handleAddTeam}
              handleUpdateProjectProductRole={handleUpdateProjectProductRole}
              handleAddProject={handleAddProject}
              handleRemoveProjectWithRoles={handleRemoveProjectWithRoles}
              handleToggleFavoriteOrg={handleToggleFavoriteOrg}
            />
          </Fragment>
        ))}
        <TableRow>
          <TableCell colspan={5} />
        </TableRow>
      </TableBody>
    </Table>
  );
};

export default memo(PermissionsTable);
